/**
 * CompanyContactPicker — type-ahead search for companies and contacts.
 *
 * Props:
 *   companyName       — current selected company name
 *   contactEmail      — current selected contact email
 *   onCompanyChange   — (name) => void
 *   onCompanyIdChange — (id) => void  (optional, receives company UUID)
 *   onContactChange   — (email) => void
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Building2, User, Search, X } from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { fetchCompanies, fetchContacts } from "../services/api";

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function CompanyContactPicker({
  companyName = "",
  contactEmail = "",
  onCompanyChange,
  onCompanyIdChange,
  onContactChange,
}) {
  const [companySearch, setCompanySearch] = useState(companyName);
  const [contactSearch, setContactSearch] = useState(contactEmail);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const companyRef = useRef(null);
  const contactRef = useRef(null);

  const debouncedCompany = useDebounce(companySearch);
  const debouncedContact = useDebounce(contactSearch);

  // Fetch company suggestions
  useEffect(() => {
    if (!debouncedCompany || debouncedCompany.length < 2) {
      setCompanySuggestions([]);
      return;
    }
    fetchCompanies({ search: debouncedCompany })
      .then((data) => setCompanySuggestions((data.companies || []).slice(0, 10)))
      .catch(() => setCompanySuggestions([]));
  }, [debouncedCompany]);

  // Fetch contact suggestions
  useEffect(() => {
    if (!debouncedContact || debouncedContact.length < 2) {
      setContactSuggestions([]);
      return;
    }
    fetchContacts({ search: debouncedContact, limit: 10 })
      .then((data) => setContactSuggestions((data.contacts || []).slice(0, 10)))
      .catch(() => setContactSuggestions([]));
  }, [debouncedContact]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e) {
      if (companyRef.current && !companyRef.current.contains(e.target)) {
        setShowCompanyDropdown(false);
      }
      if (contactRef.current && !contactRef.current.contains(e.target)) {
        setShowContactDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectCompany = useCallback((name, id) => {
    setCompanySearch(name);
    onCompanyChange(name);
    if (id) onCompanyIdChange?.(id);
    setShowCompanyDropdown(false);
  }, [onCompanyChange, onCompanyIdChange]);

  const selectContact = useCallback((contact) => {
    const label = `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || contact.email;
    setContactSearch(label);
    onContactChange(contact.email);
    // Also set company if available
    if (contact.company && !companyName) {
      setCompanySearch(contact.company);
      onCompanyChange(contact.company);
    }
    setShowContactDropdown(false);
  }, [onContactChange, onCompanyChange, companyName]);

  const inputStyle = {
    width: "100%",
    padding: "8px 12px 8px 32px",
    fontSize: 13,
    fontFamily: fonts.body,
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    background: colors.bg,
    color: colors.text,
    outline: "none",
    boxSizing: "border-box",
  };

  const dropdownStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    zIndex: 100,
    maxHeight: 200,
    overflow: "auto",
  };

  const itemStyle = {
    padding: "8px 12px",
    fontSize: 13,
    fontFamily: fonts.body,
    cursor: "pointer",
    border: "none",
    background: "transparent",
    width: "100%",
    textAlign: "left",
    color: colors.text,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Company picker */}
      <div ref={companyRef} style={{ position: "relative" }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, display: "block" }}>
          Company
        </label>
        <div style={{ position: "relative" }}>
          <Building2 size={14} color={colors.textSecondary} style={{ position: "absolute", left: 10, top: 10 }} />
          <input
            type="text"
            value={companySearch}
            onChange={(e) => {
              setCompanySearch(e.target.value);
              onCompanyChange(e.target.value);
              setShowCompanyDropdown(true);
            }}
            onFocus={() => setShowCompanyDropdown(true)}
            placeholder="Search companies..."
            style={inputStyle}
          />
          {companySearch && (
            <X
              size={14}
              color={colors.textSecondary}
              style={{ position: "absolute", right: 10, top: 10, cursor: "pointer" }}
              onClick={() => { setCompanySearch(""); onCompanyChange(""); }}
            />
          )}
        </div>
        {showCompanyDropdown && companySuggestions.length > 0 && (
          <div style={dropdownStyle}>
            {companySuggestions.map((c) => (
              <button
                key={c.id || c.name}
                onClick={() => selectCompany(c.name, c.id)}
                style={itemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 11, color: colors.textSecondary }}>
                  {c.contacts?.length || 0} contact{c.contacts?.length !== 1 ? "s" : ""}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Contact picker */}
      <div ref={contactRef} style={{ position: "relative" }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, display: "block" }}>
          Contact
        </label>
        <div style={{ position: "relative" }}>
          <User size={14} color={colors.textSecondary} style={{ position: "absolute", left: 10, top: 10 }} />
          <input
            type="text"
            value={contactSearch}
            onChange={(e) => {
              setContactSearch(e.target.value);
              setShowContactDropdown(true);
            }}
            onFocus={() => setShowContactDropdown(true)}
            placeholder="Search contacts..."
            style={inputStyle}
          />
          {contactSearch && (
            <X
              size={14}
              color={colors.textSecondary}
              style={{ position: "absolute", right: 10, top: 10, cursor: "pointer" }}
              onClick={() => { setContactSearch(""); onContactChange(""); }}
            />
          )}
        </div>
        {showContactDropdown && contactSuggestions.length > 0 && (
          <div style={dropdownStyle}>
            {contactSuggestions.map((c) => (
              <button
                key={c.id || c.email}
                onClick={() => selectContact(c)}
                style={itemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgHover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ fontWeight: 600 }}>
                  {`${c.firstName || ""} ${c.lastName || ""}`.trim() || c.email}
                </div>
                <div style={{ fontSize: 11, color: colors.textSecondary }}>
                  {c.email}{c.company ? ` — ${c.company}` : ""}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
